// v1.3.10 — handing a small file to the person: the share sheet where the
// browser shares files (iPhone: Save to Files, AirDrop, Mail…), a plain
// download everywhere else. Nothing is uploaded and nothing is kept.

export type SaveResult = "shared" | "downloaded" | "cancelled";

/** A plain download: the browser's own "save this file". (v1.3.14: text or bytes.) */
export function downloadFile(name: string, text: string | Uint8Array, mime: string): void {
  const url = URL.createObjectURL(new Blob([text as BlobPart], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Share sheet first; a download when the browser will not share this kind of file. */
export async function saveFile(name: string, text: string | Uint8Array, mime: string): Promise<SaveResult> {
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  try {
    const file = new File([text as BlobPart], name, { type: mime });
    if (typeof nav.share === "function" && nav.canShare?.({ files: [file] })) {
      try {
        await nav.share({ files: [file] });
        return "shared";
      } catch (e) {
        if ((e as DOMException)?.name === "AbortError") return "cancelled";
        // anything else: fall through to the download
      }
    }
  } catch {
    // no File constructor, or canShare threw: download
  }
  downloadFile(name, text, mime);
  return "downloaded";
}

/** 1.5.62 (Alexander, 5 Nov) — the share sheet with the file, a subject (the share's title; iOS Mail doesn't use it — seen
 *  5 Nov) and a text: Mail, WhatsApp, Files, AirDrop. 1.5.64 ("Save or share"): where a browser won't share files, the file is
 *  downloaded, as saveFile does (1.5.62 also opened the mail app). */
export async function shareWithText(name: string, data: Uint8Array, mime: string, subject: string, body: string): Promise<SaveResult> {
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  try {
    const file = new File([data as BlobPart], name, { type: mime });
    const share = { files: [file], title: subject, text: body };
    if (typeof nav.share === "function" && nav.canShare?.(share)) {
      try {
        await nav.share(share);
        return "shared";
      } catch (e) {
        if ((e as DOMException)?.name === "AbortError") return "cancelled";
      }
    }
  } catch {
    // no File constructor, or canShare threw
  }
  downloadFile(name, data, mime);
  return "downloaded";
}
